import React from 'react'

import * as c from '../../../common'
import * as cp from '../../../app/components'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { v1 as uuid } from 'uuid'

import Dialog from '../Dialog'
import PageReport from '../PageReport'
import Pad from '../Pad'

//@ts-ignore
import styles from './TestimonialVideoPage.module.css'

export interface TestimonialVideoPageProps {
  company: string
  customerID: string
  existingItemIDs: string[]
}

const MAX_RECORD_SECONDS = 300 // 5 minutes

const canUseInBrowserRecording: boolean =
  typeof MediaRecorder !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)

function getBestMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/mp4',
    'video/webm',
  ]
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t
  }
  return ''
}

function formatSeconds(secs: number): string {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

type RecordingState = 'idle' | 'recording' | 'preview'

export default React.memo<TestimonialVideoPageProps>(
  function TestimonialVideoPage({ company, customerID, existingItemIDs }) {
    const [dialogOpen, setDialogOpen] = React.useState(false)
    const [recordingState, setRecordingState] =
      React.useState<RecordingState>('idle')
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
    const [secondsLeft, setSecondsLeft] = React.useState(MAX_RECORD_SECONDS)

    const streamRef = React.useRef<MediaStream | null>(null)
    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null)
    const chunksRef = React.useRef<Blob[]>([])
    const liveVideoRef = React.useRef<HTMLVideoElement>(null)
    const fileInputRef = React.useRef<HTMLInputElement>(null)
    const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

    const buttonSx = React.useMemo(
      () => ({
        alignSelf: 'center',
        backgroundColor: c.companyColors[company],
        '&:hover': { backgroundColor: c.companyColors[company] },
      }),
      [company],
    )

    // Watch testimonial_video items in Redux
    const selectTestimonialMedia = React.useMemo(() => c.makeSelectMedia(), [])
    const selectorArgs = React.useMemo(
      () => ({ customerID, mediaKind: 'testimonial_video' as c.MediaKind }),
      [customerID],
    )
    const testimonialMedia = c.useSelector((_) =>
      selectTestimonialMedia(_, selectorArgs),
    )

    const mediaValues = Object.values(testimonialMedia)
    const uploadedItem = mediaValues.find((item) => item.status === 'uploaded')
    const pendingItem = mediaValues.find(
      (item) =>
        item.status === 'pending' || item.status === 'uploading',
    )
    const isPending = !!pendingItem
    const uploadPercent = pendingItem?.progress ?? 0
    const errorItem = mediaValues.find(
      (item) => item.err !== '' && item.status !== 'uploaded',
    )
    const videoUrl = uploadedItem ? c.getMediaItemSrc(uploadedItem) : null

    // Keep live video element synced with camera stream
    React.useEffect(() => {
      if (
        liveVideoRef.current &&
        streamRef.current &&
        liveVideoRef.current.srcObject !== streamRef.current
      ) {
        liveVideoRef.current.srcObject = streamRef.current
      }
    })

    const clearTimer = React.useCallback(() => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }, [])

    const stopStream = React.useCallback(() => {
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }, [])

    const closeDialog = React.useCallback(() => {
      clearTimer()
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      stopStream()
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
      setRecordingState('idle')
      setSecondsLeft(MAX_RECORD_SECONDS)
      chunksRef.current = []
      setDialogOpen(false)
    }, [clearTimer, previewUrl, stopStream])

    const startCameraStream = React.useCallback(async (): Promise<boolean> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        })
        streamRef.current = stream
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream
        }
        return true
      } catch {
        return false
      }
    }, [])

    const deleteExistingItems = React.useCallback(() => {
      for (const mediaItemID of existingItemIDs) {
        c.dispatch(
          c.requestedMediaDeletion({
            customerID,
            mediaItemID,
            mediaKind: 'testimonial_video',
          }),
        )
      }
    }, [customerID, existingItemIDs])

    const openDialog = React.useCallback(async () => {
      setRecordingState('idle')
      setPreviewUrl(null)
      setSecondsLeft(MAX_RECORD_SECONDS)
      chunksRef.current = []
      setDialogOpen(true)
      const ok = await startCameraStream()
      if (!ok) closeDialog()
    }, [closeDialog, startCameraStream])

    const handleRecordButton = React.useCallback(() => {
      // Existing item is deleted once a new recording/file is actually
      // confirmed (uploadRecordedVideo / handleFileInput), not here - the
      // user has not recorded or picked anything yet at this point, and
      // closing the dialog or denying camera/file access must not wipe out
      // their existing testimonial with nothing to replace it.
      if (canUseInBrowserRecording) {
        openDialog()
      } else {
        fileInputRef.current?.click()
      }
    }, [openDialog])

    const stopRecording = React.useCallback(() => {
      clearTimer()
      mediaRecorderRef.current?.stop()
    }, [clearTimer])

    const startRecording = React.useCallback(() => {
      if (!streamRef.current) return

      const mimeType = getBestMimeType()
      const options: MediaRecorderOptions = {
        videoBitsPerSecond: 1_000_000,
      }
      if (mimeType) options.mimeType = mimeType

      const recorder = new MediaRecorder(streamRef.current, options)

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        clearTimer()
        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'video/webm',
        })
        const url = URL.createObjectURL(blob)
        setPreviewUrl(url)
        setRecordingState('preview')
        stopStream()
      }

      recorder.start(100)
      mediaRecorderRef.current = recorder
      setRecordingState('recording')

      // Start countdown timer
      let secs = MAX_RECORD_SECONDS
      timerRef.current = setInterval(() => {
        secs -= 1
        setSecondsLeft(secs)
        if (secs <= 0) {
          stopRecording()
        }
      }, 1000)
    }, [clearTimer, stopRecording, stopStream])

    const reRecord = React.useCallback(async () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
      chunksRef.current = []
      setSecondsLeft(MAX_RECORD_SECONDS)
      setRecordingState('idle')
      const ok = await startCameraStream()
      if (!ok) closeDialog()
    }, [closeDialog, previewUrl, startCameraStream])

    const dispatchUpload = React.useCallback(
      (dataUrl: string) => {
        c.dispatch(
          c.requestedMediaUpload({
            items: [
              {
                customerID,
                name: uuid(),
                ofWhat: 'testimonial_video',
                path: dataUrl,
                thumbnail: '',
                type: 'video',
              },
            ],
          }),
        )
      },
      [customerID],
    )

    const uploadRecordedVideo = React.useCallback(async () => {
      if (!chunksRef.current.length) return

      const mimeType = getBestMimeType() || 'video/webm'
      const blob = new Blob(chunksRef.current, { type: mimeType })

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      deleteExistingItems()
      dispatchUpload(dataUrl)
      closeDialog()
    }, [closeDialog, deleteExistingItems, dispatchUpload])

    const handleFileInput = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
          deleteExistingItems()
          dispatchUpload(reader.result as string)
        }
        reader.readAsDataURL(file)
        e.target.value = ''
      },
      [deleteExistingItems, dispatchUpload],
    )

    return (
      <PageReport
        autoHeight
        company={company}
        companyBorder
        noPDF
        title="Customer Testimonial"
      >
        <input
          ref={fileInputRef}
          accept="video/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
          type="file"
        />

        {isPending && (
          <div className={styles['uploading-container']}>
            <CircularProgress
              size={56}
              value={uploadPercent}
              variant={uploadPercent > 0 ? 'determinate' : 'indeterminate'}
            />
            <cp.Text size={16}>{`${uploadPercent}%`}</cp.Text>
          </div>
        )}

        {!isPending && errorItem && (
          <div className={styles['error-container']}>
            <cp.Text bold size={14}>
              Upload failed: {errorItem.err}
            </cp.Text>
            <Pad amt={8} />
            <Button onClick={handleRecordButton} sx={buttonSx} variant="contained">
              Retry
            </Button>
          </div>
        )}

        {!isPending && !errorItem && videoUrl && (
          <div className={styles['video-container']}>
            <video
              className={styles['video-player']}
              controls
              src={videoUrl}
            />
            <Pad amt={24} />
            <Button onClick={handleRecordButton} sx={buttonSx} variant="contained">
              Re-record Testimonial
            </Button>
          </div>
        )}

        {!isPending && !errorItem && !videoUrl && (
          <div className={styles['empty-container']}>
            <cp.Text bold size={18}>
              No testimonial recorded yet
            </cp.Text>
            <Pad amt={12} />
            <cp.Text size={14}>
              Record a short video testimonial from your customer.
            </cp.Text>
            <Pad amt={32} />
            <Button onClick={handleRecordButton} sx={buttonSx} variant="contained">
              Record Testimonial
            </Button>
          </div>
        )}

        {canUseInBrowserRecording && (
          <Dialog
            fullWidth
            onClose={closeDialog}
            open={dialogOpen}
            title="Record Customer Testimonial"
          >
            <div className={styles['dialog-body']}>
              {recordingState !== 'preview' && (
                <video
                  ref={liveVideoRef}
                  autoPlay
                  className={styles['live-video']}
                  muted
                  playsInline
                />
              )}

              {recordingState === 'idle' && (
                <>
                  <Pad amt={16} />
                  <cp.Button on="canvas" onPress={startRecording} shrink>
                    Start Recording
                  </cp.Button>
                </>
              )}

              {recordingState === 'recording' && (
                <>
                  <Pad amt={12} />
                  <div className={styles['recording-row']}>
                    <div className={styles['recording-dot']} />
                    <cp.Text bold size={14}>
                      {`Recording — ${formatSeconds(secondsLeft)} remaining`}
                    </cp.Text>
                  </div>
                  <Pad amt={12} />
                  <cp.Button on="canvas" onPress={stopRecording} shrink>
                    Stop Recording
                  </cp.Button>
                </>
              )}

              {recordingState === 'preview' && previewUrl && (
                <>
                  <video
                    className={styles['preview-video']}
                    controls
                    src={previewUrl}
                  />
                  <Pad amt={16} />
                  <div className={styles['preview-buttons']}>
                    <cp.Button on="canvas" onPress={reRecord} shrink>
                      Re-record
                    </cp.Button>
                    <cp.Button on="canvas" onPress={uploadRecordedVideo} shrink>
                      Upload
                    </cp.Button>
                  </div>
                </>
              )}
            </div>
          </Dialog>
        )}
      </PageReport>
    )
  },
)
